import { Mark, mergeAttributes } from "@tiptap/core";
import { filterStyle, PRESERVED_STYLE_PROPS } from "./styleProps";

/**
 * Keeps `<span style="...">` runs.
 *
 * NOTICE_VALID_ELEMENTS allows span[style], and the notices use it for
 * character-level formatting that no mark covers - a font size, a colour, a
 * letter-spaced heading. Without this the span is unwrapped and its styling
 * is gone; bold and italic survive only because StarterKit happens to
 * recognise `font-weight`/`font-style` as its own marks.
 *
 * This is a deliberately minimal stand-in for @tiptap/extension-text-style:
 * it keeps exactly the properties styleProps.js allows and adds no dependency.
 */
export const PreservedSpan = Mark.create({
  name: "preservedSpan",

  addOptions() {
    return { properties: PRESERVED_STYLE_PROPS };
  },

  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class") || null,
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
      style: {
        default: null,
        parseHTML: (element) =>
          filterStyle(element.getAttribute("style"), this.options.properties) ||
          null,
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[style]",
        // A span whose style is entirely outside the allowlist carries nothing
        // worth keeping - let it unwrap rather than litter the document with
        // empty marks.
        getAttrs: (element) =>
          filterStyle(element.getAttribute("style"), this.options.properties)
            ? {}
            : false,
      },
      // A span carrying only a class is layout too - the factsheet's own
      // stylesheet targets it. See factsheetPreset.
      {
        tag: "span[class]",
        getAttrs: (element) => (element.getAttribute("class") ? {} : false),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
});

export default PreservedSpan;
